import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import type { EnvironmentDetail } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Textarea } from "../../shared/ui";

/**
 * Adding or editing an environment (§63).
 *
 * <p>The code cannot be changed after creation, and that is deliberate rather than an oversight.
 * Deployments, prerequisites and test records are read back by environment for as long as the
 * history lasts; renaming the display name is harmless, but changing the code underneath them
 * would make past records refer to something that no longer exists by that name.
 */
export function EnvironmentDrawer({
  environment,
  open,
  onClose,
  onSaved,
}: {
  environment: EnvironmentDetail | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [production, setProduction] = useState(false);
  const [displayOrder, setDisplayOrder] = useState("50");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setCode(environment?.code ?? "");
    setName(environment?.name ?? "");
    setDescription(environment?.description ?? "");
    setProduction(environment?.production ?? false);
    setDisplayOrder(String(environment?.displayOrder ?? 50));
    setError(null);
  }, [open, environment]);

  const canSubmit =
    name.trim() !== "" && (environment != null || code.trim() !== "");

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() === "" ? null : description.trim(),
        production,
        displayOrder: Number(displayOrder) || 0,
      };
      if (environment) {
        await api.put(`/api/v1/environments/${environment.id}`, body);
      } else {
        await api.post("/api/v1/environments", { ...body, code: code.trim() });
      }
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save that.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={environment ? "Edit environment" : "New environment"}
      subtitle="Somewhere a release can be deployed to."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : environment ? "Save" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field
          label="Code"
          required={environment == null}
          hint={
            environment
              ? "Fixed once created — the history refers to this environment by it."
              : "Upper-cased and underscored automatically. PROD_EU, CLIENT_ACME."
          }
        >
          <Input
            autoFocus={environment == null}
            value={code}
            disabled={environment != null}
            onChange={(event) => setCode(event.target.value)}
            placeholder="PROD_EU"
          />
        </Field>

        <Field label="Name" required hint="What people call it on screen.">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Production (EU)"
          />
        </Field>

        <Field label="Description">
          <Textarea
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <label className="flex items-start gap-2 text-sm text-ink-800">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-ink-300"
            checked={production}
            onChange={(event) => setProduction(event.target.checked)}
          />
          <span>
            Treat this as production
            <span className="block text-xs text-ink-500">
              This is what decides how strictly a release to it is governed — not whether the name
              happens to say "production".
            </span>
          </span>
        </label>

        <Field label="Order" hint="Where it sits in the list. Lower comes first.">
          <Input
            type="number"
            min={0}
            value={displayOrder}
            onChange={(event) => setDisplayOrder(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
